/**
 * The sky, as pure arithmetic.
 *
 * Every function here is total, O(1) and free of DOM access — which is the whole
 * point. The design had this maths inline inside a function that also mutated the
 * document, so none of it could be tested. Pulled out, it is the part of the
 * feature that can be wrong without looking wrong, and it is tested exhaustively
 * at every table boundary.
 */

export type ResolvedTheme = 'claro' | 'escuro'

export interface SkyStop {
  readonly h: number
  /** Top, middle, horizon. */
  readonly c: readonly [string, string, string]
}

/** Night-biased table, used when the resolved theme is dark. */
export const SKY_DARK: readonly SkyStop[] = Object.freeze([
  { h: 0, c: ['#0A0F24', '#121A38', '#1D2748'] },
  { h: 4.6, c: ['#12183A', '#26294E', '#3A3350'] },
  { h: 6.2, c: ['#1E2C46', '#43334A', '#553B3E'] },
  { h: 7.6, c: ['#243449', '#2E3C4C', '#3E4348'] },
  { h: 12, c: ['#26384E', '#2E4252', '#3A4550'] },
  { h: 16.6, c: ['#26364C', '#3A3C4A', '#4A4040'] },
  { h: 18.2, c: ['#20283F', '#48303C', '#56382F'] },
  { h: 19.6, c: ['#141A38', '#2A2547', '#3A2C42'] },
  { h: 21, c: ['#0C1128', '#141C3C', '#20294C'] },
  { h: 24, c: ['#0A0F24', '#121A38', '#1D2748'] },
])

/** Daylight table, used when the resolved theme is light. */
export const SKY_LIGHT: readonly SkyStop[] = Object.freeze([
  { h: 0, c: ['#D6DAEF', '#E4E2F0', '#F7F1E6'] },
  { h: 4.6, c: ['#DAD6EF', '#EFDCE8', '#FBEEE2'] },
  { h: 6.2, c: ['#D5E1F2', '#FBDBC4', '#FDEFDC'] },
  { h: 7.6, c: ['#D1E5F7', '#E6EFF8', '#FBF6EC'] },
  { h: 12, c: ['#CBE3F8', '#E2EFFA', '#FBF6EC'] },
  { h: 16.6, c: ['#D6E5F5', '#F0E9DC', '#FBF3E4'] },
  { h: 18.2, c: ['#D7DBF0', '#FBD7C0', '#FCECD8'] },
  { h: 19.6, c: ['#D0D1EC', '#E3D8EC', '#F8EEE4'] },
  { h: 21, c: ['#D4D7EE', '#E1E0F0', '#F7F1E6'] },
  { h: 24, c: ['#D6DAEF', '#E4E2F0', '#F7F1E6'] },
])

export function clamp(value: number, min: number, max: number): number {
  return Math.min(max, Math.max(min, value))
}

export function hexToRgb(hex: string): readonly [number, number, number] {
  const packed = Number.parseInt(hex.slice(1), 16)

  return [(packed >> 16) & 0xff, (packed >> 8) & 0xff, packed & 0xff]
}

function channel(from: number, to: number, t: number): string {
  return Math.round(from + (to - from) * t)
    .toString(16)
    .padStart(2, '0')
}

/**
 * Linear channel interpolation. `t` is clamped by the caller, not here.
 *
 * Destructured rather than mapped: `map` over a tuple loses its length, so every
 * channel read would need an `?? fallback` that no test could ever reach.
 */
export function mix(from: string, to: string, t: number): string {
  const [r1, g1, b1] = hexToRgb(from)
  const [r2, g2, b2] = hexToRgb(to)

  return `#${channel(r1, r2, t)}${channel(g1, g2, t)}${channel(b1, b2, t)}`
}

/**
 * The pair of stops bracketing `hour`, or `undefined` for an empty table.
 *
 * Carried as values through the walk rather than resolved by index: indexed
 * access into a `readonly SkyStop[]` is `SkyStop | undefined` whatever the real
 * length, and each of those guards would be a branch nothing can execute.
 */
function segment(
  table: readonly SkyStop[],
  hour: number,
): readonly [SkyStop, SkyStop] | undefined {
  let previous: SkyStop | undefined

  for (const stop of table) {
    if (previous !== undefined && hour <= stop.h) {
      return [previous, stop]
    }
    previous = stop
  }

  // Reached only for an empty table, or a single-stop one that the hour is past.
  return previous === undefined ? undefined : [previous, previous]
}

/**
 * Piecewise-linear lookup.
 *
 * The walk is bounded by the table length, a compile-time constant of ten — it
 * reads like a loop but it is O(1) with a constant of 10.
 *
 * Exported taking the table so the degenerate cases stay reachable from a test.
 */
export function paletteFrom(
  table: readonly SkyStop[],
  hour: number,
): readonly [string, string, string] {
  const bounded = clamp(hour, 0, 24)
  const pair = segment(table, bounded)

  if (pair === undefined) {
    return ['#000000', '#000000', '#000000']
  }

  const [from, to] = pair
  const span = to.h - from.h
  const t = span === 0 ? 0 : clamp((bounded - from.h) / span, 0, 1)

  return [
    mix(from.c[0], to.c[0], t),
    mix(from.c[1], to.c[1], t),
    mix(from.c[2], to.c[2], t),
  ]
}

export function skyPalette(
  hour: number,
  theme: ResolvedTheme,
): readonly [string, string, string] {
  return paletteFrom(theme === 'escuro' ? SKY_DARK : SKY_LIGHT, hour)
}

export type SkyPhase = 'madrugada' | 'amanhecer' | 'manhã' | 'tarde' | 'fim de tarde' | 'noite'

export function phase(hour: number): SkyPhase {
  if (hour < 5) {
    return 'madrugada'
  }
  if (hour < 7) {
    return 'amanhecer'
  }
  if (hour < 12) {
    return 'manhã'
  }
  if (hour < 17) {
    return 'tarde'
  }
  if (hour < 19.5) {
    return 'fim de tarde'
  }
  return 'noite'
}

export function greeting(hour: number): string {
  if (hour < 5) {
    return 'Boa noite'
  }
  if (hour < 12) {
    return 'Bom dia'
  }
  if (hour < 18) {
    return 'Boa tarde'
  }
  return 'Boa noite'
}

export interface BodyPosition {
  readonly x: number
  readonly y: number
  readonly opacity: number
}

/** Height of the sun above the horizon, as a sine from -1 to 1. */
function solarAltitude(hour: number): number {
  return Math.sin((Math.PI * (hour - 6)) / 12)
}

export function sunPosition(hour: number): BodyPosition {
  const altitude = solarAltitude(hour)
  const progress = clamp((hour - 6) / 12, 0, 1)

  return {
    x: -60 + 1320 * progress,
    y: 560 - 470 * Math.max(altitude, -0.1),
    opacity: clamp((altitude + 0.06) / 0.1, 0, 1),
  }
}

/**
 * The moon rises at 18:00 and wraps through midnight.
 *
 * `(((hour - 18) % 24) + 24) % 24` is the expression most likely to be transcribed
 * wrong — the double modulo is what keeps the result non-negative for hours before
 * 18:00, where a single `%` would return a negative. Tested on both sides of the
 * wrap.
 */
export function moonPosition(hour: number): BodyPosition {
  const wrapped = ((((hour - 18) % 24) + 24) % 24) / 12
  const progress = clamp(wrapped, 0, 1)
  const altitude = Math.sin(Math.PI * progress)
  const solar = solarAltitude(hour)

  return {
    x: -60 + 1320 * progress,
    y: 560 - 440 * Math.max(altitude, -0.1),
    // Visible only when the moon is up AND the sun is not.
    opacity: clamp((altitude + 0.03) / 0.12, 0, 1) * clamp((-solar + 0.05) / 0.14, 0, 1),
  }
}

export function starOpacity(hour: number): number {
  return clamp((-solarAltitude(hour) - 0.02) / 0.16, 0, 1)
}

export interface Aurora {
  readonly x: number
  readonly y: number
  readonly opacity: number
  readonly color: string
}

/** Dawn is cooler than dusk, and both are muted under the dark palette. */
const AURORA_COLOURS = Object.freeze({
  claro: Object.freeze({ manha: '#FFC98A', tarde: '#FF9E6B' }),
  escuro: Object.freeze({ manha: '#4E3A2C', tarde: '#5A3728' }),
})

/** The warm glow that tracks the sun along the horizon at dawn and dusk. */
export function aurora(hour: number, theme: ResolvedTheme): Aurora {
  const altitude = solarAltitude(hour)
  const dark = theme === 'escuro'
  const colours = dark ? AURORA_COLOURS.escuro : AURORA_COLOURS.claro

  return {
    x: sunPosition(hour).x,
    y: 560 - 90 * Math.max(altitude, 0),
    opacity: clamp(1 - Math.abs(altitude) / 0.3, 0, 1) * (dark ? 0.3 : 0.7),
    color: hour < 12 ? colours.manha : colours.tarde,
  }
}

export interface BodyColours {
  readonly sun: string
  readonly moon: string
  readonly star: string
}

export function bodyColours(theme: ResolvedTheme): BodyColours {
  return theme === 'escuro'
    ? { sun: '#FFD35E', moon: '#F7F3E6', star: '#FDFBF2' }
    : { sun: '#F0AE3C', moon: '#9FA4CC', star: '#9096C0' }
}

/** `HH:MM`, zero-padded. */
export function formatClock(hour: number): string {
  const bounded = clamp(hour, 0, 23.9999)
  const hours = Math.floor(bounded)
  const minutes = Math.floor((bounded - hours) * 60)

  return `${String(hours).padStart(2, '0')}:${String(minutes).padStart(2, '0')}`
}

/** Decimal hours from a `Date`, which is what every function above takes. */
export function hourOf(date: Date): number {
  return date.getHours() + date.getMinutes() / 60 + date.getSeconds() / 3600
}
